import React from "react";
import { formatISO, parseISO } from "date-fns";
import { useTypedSelector } from "../../../../hooks/useTypedSelector";
import { useActions } from "../../../../hooks/useActions";
import styles from "./DatePicker.module.scss";
import { useTranslation } from "react-i18next";
import CustomDatePicker from "../../../CustomDatePicker";
import useIsFilterActive from "../../../../hooks/useIsFilterActive";
import { FilterIdsEnum } from "../../../../store/reducers/_maps/main/mapFilters/types";
import LoginNotificationPopup from "../../../../share/NotificationPopInformation/LoginNotificationPopup/LoginNotificationPopup";
import useStoreSnippets from "../../../../hooks/useStoreSnippets";
import { useTipsContext } from "../../../../hooks/useTips";

interface IProps {
  filterId?: FilterIdsEnum;
  showTemplates?: boolean;
  modalBodyRef?: React.MutableRefObject<HTMLDivElement | undefined>;
}

const DatePicker = ({
  showTemplates,
  modalBodyRef,
  filterId = "date",
}: IProps) => {
  const { from_date, to_date } = useTypedSelector((state) => state.mapFilters);
  const isFilterActive = useIsFilterActive(filterId);
  const { setFromDate, setToDate, applyFilter } = useActions();
  const tipId = "loginToUseDateFilterMain";
  const curr = new Date();
  const currLast = new Date();
  const setLastOpenedTipId = useTipsContext().setLastOpenedTipId;

  const setStartDate = (date: Date | null) => {
    if (!date) {
      return;
    }
    setFromDate(formatISO(date, { representation: "date" }));
  };

  const setEndDate = (date: Date | null) => {
    if (!date) {
      return;
    }
    setToDate(formatISO(date, { representation: "date" }));
  };

  const setCurrentWeek = () => {
    const first = curr.getDate() - curr.getDay();
    const firstDay = new Date(curr.setDate(first + 1));
    const lastDay = new Date(currLast.setDate(first + 7));
    setStartDate(firstDay);
    setEndDate(lastDay);
    setFromDate(formatISO(firstDay, { representation: "date" }));
    setToDate(formatISO(lastDay, { representation: "date" }));
    applyFilter();
  };

  const setPrevWeek = () => {
    const first = curr.getDate() - curr.getDay() - 7;
    const firstDay = new Date(curr.setDate(first + 1));
    const lastDay = new Date(currLast.setDate(first + 7));
    setStartDate(firstDay);
    setEndDate(lastDay);
    setFromDate(formatISO(firstDay, { representation: "date" }));
    setToDate(formatISO(lastDay, { representation: "date" }));
    applyFilter();
  };

  const setCurrentMonth = () => {
    const firstDayOfMonth = new Date(curr.getFullYear(), curr.getMonth(), 1);
    const lastDayOfMonth = new Date(curr.getFullYear(), curr.getMonth() + 1, 0);
    setStartDate(firstDayOfMonth);
    setEndDate(lastDayOfMonth);
    setFromDate(formatISO(firstDayOfMonth, { representation: "date" }));
    setToDate(formatISO(lastDayOfMonth, { representation: "date" }));
    applyFilter();
  };

  const setPrevMonth = () => {
    const firstDayOfMonth = new Date(
      curr.getFullYear(),
      curr.getMonth() - 1,
      1
    );
    const lastDayOfMonth = new Date(curr.getFullYear(), curr.getMonth(), 0);
    setStartDate(firstDayOfMonth);
    setEndDate(lastDayOfMonth);
    setFromDate(formatISO(firstDayOfMonth, { representation: "date" }));
    setToDate(formatISO(lastDayOfMonth, { representation: "date" }));
    applyFilter();
  };

  const { t: tPeriod } = useTranslation("translation", {
    keyPrefix: "mainPage.mapFilters.aimap.periodFilter",
  });
  const { t: tNotification } = useTranslation("translation", {
    keyPrefix: "notifications",
  });
  const { isGuestAccount } = useStoreSnippets();

  return (
    <>
      <LoginNotificationPopup
        id={tipId}
        message={tNotification("loginToUseDateFilter")}
        rightPx={-456}
        bottomPx={400}
        cornerTopPosition={100}
      />
      <div
        onClick={isGuestAccount ? () => setLastOpenedTipId(tipId) : undefined}
      >
        <div className={isGuestAccount ? styles.datePickerWrapperDisabled : ""}>
          <div
            className={!isFilterActive ? styles.filterOnMap : styles.formGroup}
          >
            <div className={styles.datePickerWrapper}>
              <CustomDatePicker
                startDate={from_date ? parseISO(from_date) : null}
                endDate={to_date ? parseISO(to_date) : null}
                setStartDate={setStartDate}
                setEndDate={setEndDate}
                modalBodyRef={modalBodyRef}
              />
            </div>
            {!isFilterActive && showTemplates ? (
              <div className={styles.calendarFilters}>
                <button
                  onClick={setCurrentWeek}
                  className={styles.dateFilterButton}
                  type="button"
                >
                  {tPeriod("currentWeek")}
                </button>
                <button
                  onClick={setPrevWeek}
                  className={styles.dateFilterButton}
                  type="button"
                >
                  {tPeriod("previousWeek")}
                </button>
                <button
                  onClick={setCurrentMonth}
                  className={styles.dateFilterButton}
                  type="button"
                >
                  {tPeriod("currentMonth")}
                </button>
                <button
                  onClick={setPrevMonth}
                  className={styles.dateFilterButton}
                  type="button"
                >
                  {tPeriod("previousMonth")}
                </button>
              </div>
            ) : isFilterActive ? (
              <div className={styles.calendarFilters}>
                <button
                  onClick={setCurrentWeek}
                  className={styles.dateFilterButton}
                  type="button"
                >
                  {tPeriod("currentWeek")}
                </button>
                <button
                  onClick={setPrevWeek}
                  className={styles.dateFilterButton}
                  type="button"
                >
                  {tPeriod("previousWeek")}
                </button>
                <button
                  onClick={setCurrentMonth}
                  className={styles.dateFilterButton}
                  type="button"
                >
                  {tPeriod("currentMonth")}
                </button>
                <button
                  onClick={setPrevMonth}
                  className={styles.dateFilterButton}
                  type="button"
                >
                  {tPeriod("previousMonth")}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
};

export default DatePicker;
